import { Component, esc } from '../core/component.js';
import { api, ApiError } from '../core/api.js';
import { formatDate } from '../core/format.js';
import { icon } from '../core/icons.js';
import { t } from '../core/i18n.js';

const STATUS_GROUPS = ['ACTIVE', 'DRAFT', 'ENDED'];

export class TenanciesView extends Component {
  constructor() {
    super();
    this.state = { tenancies: null, error: null, unread: {} };
    // Filter / sort kept off-state so input edits don't trigger full re-render
    // (which would lose focus on the search field).
    this.filters = { search: '' };
    this.sort = { field: 'start', dir: 'desc' };
  }

  template() {
    const list = this.state.tenancies;
    const counts = Array.isArray(list)
      ? list.reduce((acc, c) => { acc[c.status] = (acc[c.status] || 0) + 1; return acc; }, {})
      : {};
    const total = Array.isArray(list) ? list.length : 0;
    return `
      <section class="stack">
        <div class="panel">
          <div class="panel-head panel-head--cta">
            <div class="panel-titles">
              <div class="panel-title-eyebrow">${esc(t('tenancies.eyebrow') || 'Rental agreements')}</div>
              <h2 class="panel-title">${esc(t('tenancies.title'))}</h2>
            </div>
            <div class="panel-status">
              <a class="btn btn-primary panel-cta" href="#/tenancies/new">${icon('user-plus', { size: 'sm' })}<span>${esc(t('move_in.entry'))}</span></a>
            </div>
          </div>
          ${Array.isArray(list) && list.length > 0 ? `
            <div class="panel-body">
              <div class="tenancy-stats">
                <div class="tenancy-stat"><span class="tenancy-stat-k">${esc(t('tenancies.groups.ACTIVE'))}</span><strong>${counts.ACTIVE || 0}</strong></div>
                <div class="tenancy-stat"><span class="tenancy-stat-k">${esc(t('tenancies.groups.DRAFT'))}</span><strong>${counts.DRAFT || 0}</strong></div>
                <div class="tenancy-stat"><span class="tenancy-stat-k">${esc(t('tenancies.groups.ENDED'))}</span><strong>${counts.ENDED || 0}</strong></div>
                <div class="tenancy-stat"><span class="tenancy-stat-k">${esc(t('tenancies.total') || 'Total')}</span><strong>${total}</strong></div>
              </div>
            </div>
          ` : ''}
        </div>
        ${this.state.error ? `<div class="error-banner">${esc(this.state.error)}</div>` : ''}
        ${list === null
          ? `<div class="muted">${esc(t('tenancies.loading'))}</div>`
          : list.length === 0
            ? `<div class="empty">${esc(t('tenancies.empty'))}</div>`
            : `
              ${this._filterBarHtml()}
              <div data-list-region>${this._listHtml(this._apply(list))}</div>
            `}
      </section>
    `;
  }

  _filterBarHtml() {
    const sortArrow = (field) => this.sort.field === field
      ? (this.sort.dir === 'desc' ? icon('arrow-down', { size: 'sm' }) : icon('arrow-up', { size: 'sm' }))
      : '';
    return `
      <div class="tenancies-toolbar">
        <input class="input" type="search" data-filter="search" placeholder="${esc(t('tenancies.filters.search_placeholder'))}" value="${esc(this.filters.search)}" />
        <div class="tenancies-sort row">
          <span class="faint">${esc(t('tenancies.sort.label'))}</span>
          <button type="button" class="btn btn-ghost btn-sm${this.sort.field === 'start' ? ' is-active' : ''}" data-sort="start">
            <span>${esc(t('tenancies.sort.start'))}</span>${sortArrow('start')}
          </button>
          <button type="button" class="btn btn-ghost btn-sm${this.sort.field === 'end' ? ' is-active' : ''}" data-sort="end">
            <span>${esc(t('tenancies.sort.end'))}</span>${sortArrow('end')}
          </button>
        </div>
      </div>
    `;
  }

  _apply(list) {
    const f = this.filters;
    const search = f.search.trim().toLowerCase();
    const filtered = list.filter((c) => {
      if (!search) return true;
      const propNames = (c.units || []).map((u) => String(u.property_name || '')).filter(Boolean);
      const statusKey = String(c.status || '');
      const statusLabels = statusKey ? [statusKey, t(`tenancies.status.${statusKey}`), t(`tenancies.groups.${statusKey}`)] : [];
      const hay = [c.tenant_name, c.tenant_email, c.invitation_name, c.invitation_email, ...propNames, ...statusLabels]
        .filter(Boolean).join(' ').toLowerCase();
      return hay.includes(search);
    });
    const dir = this.sort.dir === 'asc' ? 1 : -1;
    const key = this.sort.field === 'end' ? 'end_date' : 'start_date';
    filtered.sort((a, b) => {
      const av = a[key] || '';
      const bv = b[key] || '';
      // Treat open-ended (empty end_date) as +infinity: top in desc, bottom in asc.
      if (key === 'end_date') {
        if (!av && !bv) return 0;
        if (!av) return 1 * dir;
        if (!bv) return -1 * dir;
      }
      if (av < bv) return -1 * dir;
      if (av > bv) return 1 * dir;
      return 0;
    });
    return filtered;
  }


  _listHtml(filtered) {
    if (filtered.length === 0) {
      return `<div class="empty">${esc(t('tenancies.no_match'))}</div>`;
    }
    const groups = new Map(STATUS_GROUPS.map((s) => [s, []]));
    const others = [];
    for (const c of filtered) {
      const s = String(c.status || '');
      if (groups.has(s)) groups.get(s).push(c);
      else others.push(c);
    }
    const sections = STATUS_GROUPS
      .filter((s) => groups.get(s).length > 0)
      .map((s) => this._sectionHtml(s, groups.get(s)));
    if (others.length > 0) sections.push(this._sectionHtml('OTHER', others));
    return sections.join('');
  }

  _sectionHtml(status, items) {
    const titleKey = status === 'OTHER' ? 'tenancies.groups.OTHER' : `tenancies.groups.${status}`;
    return `
      <div class="tenancies-group">
        <div class="tenancies-group-header">
          <span class="group-title">${esc(t(titleKey))}</span>
          <span class="faint">· ${items.length}</span>
        </div>
        <div class="list">${items.map((c) => this._itemHtml(c)).join('')}</div>
      </div>
    `;
  }

  _itemHtml(c) {
    const badgeClass = c.status === 'ACTIVE' ? 'badge-success'
      : c.status === 'DRAFT' ? 'badge-warning'
      : c.status === 'ENDED' ? 'badge-muted' : 'badge';
    const endLabel = c.end_date ? formatDate(c.end_date) : t('common.open');
    const awaiting = !c.tenant_user_id && c.invitation_id;
    const tenantLabel = c.tenant_name || c.tenant_email || c.invitation_name || c.invitation_email || t('tenancies.tenant_fallback');
    const propNames = Array.from(new Set((c.units || []).map((u) => u && u.property_name).filter(Boolean)));
    const propLine = propNames.length ? `<span class="faint">${esc(propNames.join(' · '))}</span>` : '';
    const unread = Number((this.state.unread || {})[c.id] || 0);
    return `
      <a class="list-item" href="#/tenancies/${esc(c.id)}">
        ${icon('file-text', { class: 'list-leading icon-faint' })}
        <div class="stack-2 grow">
          <strong>${esc(tenantLabel)}${awaiting ? ` <span class="faint">· ${esc(t('tenancies.awaiting_tenant'))}</span>` : ''}</strong>
          ${propLine}
          <span class="faint period">
            <span>${esc(formatDate(c.start_date))}</span>
            ${icon('arrow-right', { size: 'sm', class: 'icon-faint' })}
            <span>${esc(endLabel)}</span>
            <span>· ${esc(c.currency || '')}</span>
          </span>
        </div>
        ${unread > 0 ? `<span class="badge badge-danger">${esc(t('tenancies.unread_badge', { count: unread }))}</span>` : ''}
        <span class="badge ${badgeClass}">${esc(c.status)}</span>
      </a>
    `;
  }

  mount() {
    if (this.state.tenancies === null) {
      this._load();
      return;
    }
    this._wireFilters();
  }

  _wireFilters() {
    const root = this.el;
    if (!root) return;
    const search = root.querySelector('[data-filter="search"]');
    if (search) search.addEventListener('input', (e) => {
      this.filters.search = e.target.value;
      this._renderList();
    });
    root.querySelectorAll('[data-sort]').forEach((btn) => {
      btn.addEventListener('click', () => {
        const field = btn.getAttribute('data-sort');
        if (this.sort.field === field) {
          this.sort.dir = this.sort.dir === 'desc' ? 'asc' : 'desc';
        } else {
          this.sort.field = field;
          this.sort.dir = 'desc';
        }
        this._renderList();
        this._refreshSortButtons();
      });
    });
  }

  _refreshSortButtons() {
    const root = this.el;
    if (!root) return;
    root.querySelectorAll('[data-sort]').forEach((btn) => {
      const field = btn.getAttribute('data-sort');
      const active = this.sort.field === field;
      btn.classList.toggle('is-active', active);
      const arrow = btn.querySelector('.icon');
      if (arrow) arrow.remove();
      if (active) {
        const html = this.sort.dir === 'desc'
          ? icon('arrow-down', { size: 'sm' })
          : icon('arrow-up', { size: 'sm' });
        btn.insertAdjacentHTML('beforeend', html);
      }
    });
  }

  _renderList() {
    const region = this.el && this.el.querySelector('[data-list-region]');
    if (!region || !Array.isArray(this.state.tenancies)) return;
    region.innerHTML = this._listHtml(this._apply(this.state.tenancies));
  }

  async _load() {
    try {
      const res = await api.get('/tenancies');
      this.setState({ tenancies: res.data || [] });
      // Unread chat badges per tenancy; failures stay silent.
      api.get('/messages/unread').then((u) => {
        if (u && u.by_tenancy && Object.keys(u.by_tenancy).length > 0) {
          this.setState({ unread: u.by_tenancy });
        }
      }).catch(() => {});
    } catch (err) {
      this.setState({ error: err instanceof ApiError ? err.message : t('tenancies.errors.load') });
    }
  }
}